class FilterSortManager {
    constructor(options = {}) {
        this.sortSelector = document.getElementById(options.sortId || 'sortBy');
        this.filterSelectors = document.querySelectorAll('.filter-select');
        this.loader = document.getElementById(options.loaderId || 'filterLoader');
        this.selectedFiltersContainer = document.getElementById('selectedFilters');
        this.productGridId = options.productGridId || 'productGrid';
        this.onChange = options.onChange || null;
        this.currentPaginationSize = this.getPaginationSize();
        this.selectedColors = new Set();
        this.init();
    }

    init() {
        this.bindEventListeners();
        this.handleResponsivePagination();
        this.checkInitialPaginationSize();
        this.initColorFilter();
        this.updateSelectedFilters();
    }

    checkInitialPaginationSize() {
        const currentSize = this.getPaginationSize();
        const urlParams = new URLSearchParams(window.location.search);
        const urlPageSize = parseInt(urlParams.get('per_page')) || 12;
        
        if (currentSize !== urlPageSize) {
            this.updatePaginationSize(currentSize);
        }
    }

    getPaginationSize() {
        return window.innerWidth >= 1024 ? 10 : 12;
    }

    handleResponsivePagination() {
        let resizeTimeout;
        window.addEventListener('resize', () => {
            clearTimeout(resizeTimeout);
            resizeTimeout = setTimeout(() => {
                const newPaginationSize = this.getPaginationSize();
                const urlParams = new URLSearchParams(window.location.search);
                const currentUrlSize = parseInt(urlParams.get('per_page')) || 12;
                
                if (newPaginationSize !== currentUrlSize) {
                    this.updatePaginationSize(newPaginationSize);
                }
                
                this.currentPaginationSize = newPaginationSize;
            }, 250);
        });
    }

    updatePaginationSize(size) {
        const url = new URL(window.location);
        url.searchParams.set('per_page', size);
        window.history.replaceState({}, '', url);
        
        this.showLoader();
        this.handleResponse();
    }

    bindEventListeners() {
        if (this.sortSelector) {
            this.sortSelector.addEventListener('change', (e) => this.handleSortChange(e));
        }

        this.filterSelectors.forEach(filter => {
            filter.addEventListener('change', (e) => this.handleFilterChange(e));
        });
    }

    handleSortChange(event) {
        const selectedValue = event.target.value;
        const selectedText = event.target.options[event.target.selectedIndex].text;

        this.updateUrlParameter('sort_by', selectedValue);
        this.triggerUpdate('sort', { value: selectedValue, text: selectedText, event });
    }

    async handleFilterChange(event) {
        const filterParam = event.target.dataset.filterParam;
        const filterType = event.target.dataset.filterType;
        const selectedValue = event.target.value;
        const selectedText = event.target.options[event.target.selectedIndex].text;
        const originalValue = event.target.options[event.target.selectedIndex].dataset.originalValue;
        const filterElement = event.target.closest('[data-filter]');
        const currentFilterType = filterElement?.dataset.filter;

        // Update filter pills
        this.updateSelectedFilters();

        if (filterType === 'price_range' && selectedValue) {
            const [min, max] = selectedValue.split('-');
            this.updateUrlParameter(`${filterParam}.gte`, min);
            this.updateUrlParameter(`${filterParam}.lte`, max);
        } else if (filterType === 'list') {
            if (selectedValue) {
                const valueToUse = originalValue && originalValue.includes('gid://') ? originalValue : selectedValue;
                this.updateUrlParameter(filterParam, valueToUse);
            } else {
                this.removeUrlParameter(filterParam);
            }
        }

        this.updateUrlParameter('page', '1');

        // For weight changes, we'll validate colors after the response
        if (currentFilterType === 'yarn-weight' || currentFilterType === 'fiber-type') {
            // Store a flag to validate colors after reload
            this.shouldValidateColors = true;
        }

        this.triggerUpdate('filter', { 
            param: filterParam, 
            type: filterType, 
            value: selectedValue, 
            originalValue: originalValue,
            text: selectedText, 
            event
        });
    }

    validateSelectedColors() {
        const colorDropdown = document.querySelector('.color-filter-dropdown');
        const colorSelect = document.querySelector('.color-filter-select');
        if (!colorDropdown || !colorSelect) return;

        // Get currently available color options from the dropdown
        const availableColorOptions = colorDropdown.querySelectorAll('.color-option:not(.clear-all)');
        const availableOptionsMap = new Map();
        
        // Build a map of available colors with their current data
        availableColorOptions.forEach(opt => {
            const value = opt.dataset.value;
            const display = opt.dataset.display || opt.textContent.trim();
            const colorHex = opt.querySelector('.color-swatch')?.dataset.color;
            
            availableOptionsMap.set(value, {
                value: value,
                display: display,
                color: colorHex,
                param: colorSelect.dataset.filterParam
            });
        });

        // Find colors to remove and update existing ones
        const colorsToRemove = [];
        const updatedColors = new Set();
        
        this.selectedColors.forEach(colorData => {
            if (availableOptionsMap.has(colorData.value)) {
                // Color is still available, but update its data with current values
                updatedColors.add(availableOptionsMap.get(colorData.value));
            } else {
                colorsToRemove.push(colorData);
            }
        });

        // Update selected colors if there were changes
        if (colorsToRemove.length > 0 || updatedColors.size !== this.selectedColors.size) {
            this.selectedColors = updatedColors;

            // Update URL with current valid colors
            this.updateColorFiltersUrl();
            
            // Update visual state
            this.updateColorOptionsVisualState();
            this.updateSelectedFilters();
                
            this.triggerUpdate('filter', { 
                param: colorSelect.dataset.filterParam, 
                type: 'color_multi', 
                values: Array.from(this.selectedColors)
            });
        }
    }

    updateSelectedFilters() {
        if (!this.selectedFiltersContainer) return;

        // Clear existing pills
        this.selectedFiltersContainer.innerHTML = '';

        // Handle regular filters (non-color)
        this.filterSelectors.forEach(filter => {
            const value = filter.value;
            const filterLabel = filter.dataset.filterLabel || filter.name;
            const selectedOption = filter.options[filter.selectedIndex];
            const filterType = filter.closest('[data-filter]')?.dataset.filter;
            
            if (value && value !== '' && filterType !== 'color') {
                const displayText = selectedOption.textContent.trim();
                this.createFilterPill(filterLabel, displayText, filter.dataset.filterParam, value, filterType);
            }
        });

        // Handle color filters separately (multiple selections)
        if (this.selectedColors.size > 0) {
            this.selectedColors.forEach(colorData => {
                this.createFilterPill('Color', colorData.display, colorData.param, colorData.value, 'color', colorData.color);
            });
        }

        // Update color select display text
        this.updateColorSelectDisplay();
    }

    updateColorSelectDisplay() {
        const colorSelect = document.querySelector('.color-filter-select');
        if (!colorSelect) return;

        if (this.selectedColors.size === 0) {
            colorSelect.selectedIndex = 0;
            const firstOption = colorSelect.options[0];
            const originalText = colorSelect.dataset.originalPlaceholder || colorSelect.dataset.filterLabel || 'Color';
            firstOption.textContent = originalText;
        } else if (this.selectedColors.size === 1) {
            const selectedColor = Array.from(this.selectedColors)[0];
            const matchingOption = Array.from(colorSelect.options).find(option => 
                option.value === selectedColor.value || 
                option.dataset.originalValue === selectedColor.value
            );
            if (matchingOption) {
                colorSelect.selectedIndex = matchingOption.index;
            }
        } else {
            const firstOption = colorSelect.options[0];
            firstOption.textContent = `Colors (${this.selectedColors.size})`;
        }
    }

    createFilterPill(filterLabel, displayText, paramName, value, filterType, color) {
        const pill = document.createElement('div');
        pill.className = `filter-pill ${filterType === 'color' ? 'color-pill' : ''}`;
        pill.dataset.filterParam = paramName;
        pill.dataset.filterValue = value;

        let pillContent = '';
        
        if (filterType === 'color' && color) {
            pillContent += `<div class="color-swatch-pill" style="background-color: ${color};"></div>`;
        }
        
        pillContent += `<span>${displayText}</span>`;
        pillContent += `<div class="remove-filter" data-filter-param="${paramName}" data-filter-value="${value}" data-filter-type="${filterType}"></div>`;
        
        pill.innerHTML = pillContent;
        
        const removeBtn = pill.querySelector('.remove-filter');
        removeBtn.addEventListener('click', (e) => {
            e.stopPropagation();
            this.removeFilter(paramName, value, filterType);
        });

        this.selectedFiltersContainer.appendChild(pill);
    }

    removeFilter(paramName, value, filterType) {
        if (filterType === 'color') {
            const colorToRemove = Array.from(this.selectedColors).find(c => c.value === value);
            if (colorToRemove) {
                this.selectedColors.delete(colorToRemove);
                
                const colorOption = document.querySelector(`[data-value="${value}"]`);
                if (colorOption) {
                    colorOption.classList.remove('selected');
                }
                
                this.updateColorFiltersUrl();
                this.updateSelectedFilters();
                this.triggerUpdate('filter', { param: paramName, type: 'color_multi', values: Array.from(this.selectedColors) });
            }
        } else {
            const filterSelect = document.querySelector(`[data-filter-param="${paramName}"]`);
            if (filterSelect) {
                filterSelect.selectedIndex = 0;
                filterSelect.value = '';
                
                const changeEvent = new Event('change', { bubbles: true });
                filterSelect.dispatchEvent(changeEvent);
            }
        }
    }

    updateColorFiltersUrl() {
        const colorSelect = document.querySelector('.color-filter-select');
        if (!colorSelect) return;

        const param = colorSelect.dataset.filterParam;
        
        if (this.selectedColors.size === 0) {
            this.removeUrlParameter(param);
        } else {
            const colorValues = Array.from(this.selectedColors).map(c => c.value);
            this.updateUrlParameter(param, colorValues.join(','));
        }
    }

    initColorFilter() {
        const colorSelect = document.querySelector('.color-filter-select');
        const colorDropdown = document.querySelector('.color-filter-dropdown');

        if (!colorSelect || !colorDropdown) return;

        // Initialize selected colors from URL only if we don't have any yet
        if (this.selectedColors.size === 0) {
            this.initializeSelectedColorsFromUrl();
        }

        // Handle color select click to show dropdown
        colorSelect.addEventListener('click', (e) => {
            e.preventDefault();
            colorDropdown.classList.toggle('show');
        });

        // Bind color dropdown events
        this.bindColorDropdownEvents();

        // Close dropdown when clicking outside
        document.addEventListener('click', (e) => {
            if (!e.target.closest('.color-filter-wrapper')) {
                colorDropdown.classList.remove('show');
            }
        });

        // Prevent default dropdown behavior
        colorSelect.addEventListener('mousedown', (e) => {
            e.preventDefault();
        });

        // Update visual state based on current selections
        this.updateColorOptionsVisualState();
    }

    initializeSelectedColorsFromUrl() {
        const colorSelect = document.querySelector('.color-filter-select');
        if (!colorSelect) return;

        const urlParams = new URLSearchParams(window.location.search);
        const colorParam = colorSelect.dataset.filterParam;
        const colorValues = urlParams.get(colorParam);
        
        if (colorValues) {
            const values = colorValues.split(',');
            const colorOptions = document.querySelectorAll('.color-option:not(.clear-all)');
            
            values.forEach(value => {
                const matchingOption = Array.from(colorOptions).find(opt => opt.dataset.value === value);
                if (matchingOption) {
                    const display = matchingOption.dataset.display || matchingOption.textContent.trim();
                    const colorHex = matchingOption.querySelector('.color-swatch')?.dataset.color;
                    
                    this.selectedColors.add({
                        value: value,
                        display: display,
                        color: colorHex,
                        param: colorParam
                    });
                }
            });
        }
    }

    updateColorOptionsVisualState() {
        const colorOptions = document.querySelectorAll('.color-option:not(.clear-all)');
        
        colorOptions.forEach(option => {
            const value = option.dataset.value;
            const isSelected = Array.from(this.selectedColors).some(c => c.value === value);
            
            if (isSelected) {
                option.classList.add('selected');
            } else {
                option.classList.remove('selected');
            }
        });
    }

    bindColorDropdownEvents() {
        const colorOptions = document.querySelectorAll('.color-option');
        
        colorOptions.forEach(option => {
            option.addEventListener('click', (e) => {
                e.stopPropagation();
                if (option.classList.contains('clear-all')) {
                    this.clearAllColors();
                } else {
                    this.handleColorOptionClick(option);
                }
            });
        });
    }

    clearAllColors() {
        this.selectedColors.clear();
        const colorOptions = document.querySelectorAll('.color-option');
        colorOptions.forEach(opt => opt.classList.remove('selected'));
        this.updateColorFiltersUrl();
        this.updateSelectedFilters();
        this.triggerUpdate('filter', { 
            param: document.querySelector('.color-filter-select')?.dataset.filterParam, 
            type: 'color_multi', 
            values: [] 
        });
    }

    handleColorOptionClick(option) {
        const value = option.dataset.value;
        const display = option.dataset.display || option.textContent.trim();
        const colorHex = option.querySelector('.color-swatch')?.dataset.color;
        const colorSelect = document.querySelector('.color-filter-select');
        
        if (!colorSelect) return;
        
        const colorData = {
            value: value,
            display: display,
            color: colorHex,
            param: colorSelect.dataset.filterParam
        };

        // Toggle color selection
        if (option.classList.contains('selected')) {
            option.classList.remove('selected');
            const colorToRemove = Array.from(this.selectedColors).find(c => c.value === value);
            if (colorToRemove) {
                this.selectedColors.delete(colorToRemove);
            }
        } else {
            option.classList.add('selected');
            this.selectedColors.add(colorData);
        }

        this.updateColorFiltersUrl();
        this.updateSelectedFilters();
        
        this.triggerUpdate('filter', { 
            param: colorSelect.dataset.filterParam, 
            type: 'color_multi', 
            values: Array.from(this.selectedColors)
        });
    }

    updateUrlParameter(param, value) {
        const url = new URL(window.location);
        if (value && value !== '') {
            url.searchParams.set(param, value);
        } else {
            url.searchParams.delete(param);
        }
        window.history.replaceState({}, '', url);
    }

    removeUrlParameter(param) {
        const url = new URL(window.location);
        url.searchParams.delete(param);
        url.searchParams.delete(`${param}.gte`);
        url.searchParams.delete(`${param}.lte`);
        window.history.replaceState({}, '', url);
    }

    async triggerUpdate(type, data) {
        if (this.onChange) {
            this.onChange(type, data, this.loader);
        } else {
            this.showLoader();
            await this.handleResponse();
        }
    }

    showLoader() {
        if (this.loader) {
            this.loader.classList.remove("!hidden");
        }
    }

    hideLoader() {
        if (this.loader) {
            this.loader.classList.add("!hidden");
        }
    }

    async handleResponse() {
        try {
            const url = new URL(window.location);
            url.searchParams.set('section_id', 'tw-plp');
            
            const currentPageSize = this.getPaginationSize();
            if (!url.searchParams.has('per_page')) {
                url.searchParams.set('per_page', currentPageSize);
            }

            const request = await fetch(url.toString(), {
                method: 'GET',
                headers: {
                    'Content-Type': 'text/html',
                    'X-Requested-With': 'XMLHttpRequest'
                }
            });

            if (!request.ok) {
                throw new Error(`HTTP error! status: ${request.status}`);
            }

            const response = await request.text();
            
            const parser = new DOMParser();
            const doc = parser.parseFromString(response, 'text/html');
            
            const possibleSelectors = [
                '[data-section-type="tw-plp"]',
                'section[data-section-id*="tw-plp"]', 
                '.shopify-section[id*="tw-plp"]',
                '.shopify-section.section-tw-plp',
                'section.tw-plp'
            ];
            
            let existingSection = null;
            let newSection = null;
            
            for (const selector of possibleSelectors) {
                existingSection = document.querySelector(selector);
                if (existingSection) {
                    break;
                }
            }
            
            if (!existingSection) {
                existingSection = document.querySelector('#filtersWrapper')?.closest('section') || 
                                document.querySelector('#productGrid')?.closest('section');
            }
            
            newSection = doc.querySelector('section') || doc.body.firstElementChild;
            
            if (existingSection && newSection) {
                existingSection.innerHTML = newSection.innerHTML;
                this.reinitializeFilters();
            } else {
                window.location.reload();
                return;
            }

            const cleanUrl = new URL(window.location);
            cleanUrl.searchParams.delete('section_id');
            window.history.replaceState({}, '', cleanUrl);

            const event = new CustomEvent('sectionRendered', {
                detail: { 
                    sectionId: 'tw-plp',
                    url: cleanUrl.href,
                    timestamp: Date.now()
                }
            });
            document.dispatchEvent(event);
        } catch (error) {
            console.error('Error rendering section:', error);
            window.location.reload();
        } finally {
            this.hideLoader();
        }
    }

    reinitializeFilters() {
        this.filterSelectors.forEach(filter => {
            filter.removeEventListener('change', this.handleFilterChange);
        });

        this.sortSelector = document.getElementById('sortBy');
        this.filterSelectors = document.querySelectorAll('.filter-select');
        this.selectedFiltersContainer = document.getElementById('selectedFilters');
        // Don't reset selectedColors here - we want to preserve valid selections

        if (this.sortSelector) {
            this.sortSelector.addEventListener('change', (e) => this.handleSortChange(e));
        }

        this.filterSelectors.forEach(filter => {
            filter.addEventListener('change', (e) => this.handleFilterChange(e));
        });
        
        this.currentPaginationSize = this.getPaginationSize();
        this.initColorFilter();
        
        // If we should validate colors (after weight change), do it now
        if (this.shouldValidateColors) {
            this.validateSelectedColors();
            this.shouldValidateColors = false;
        }
        
        this.updateSelectedFilters();
    }

    clearAllFilters() {
        this.filterSelectors.forEach(filter => {
            filter.selectedIndex = 0;
        });

        this.selectedColors.clear();

        const colorOptions = document.querySelectorAll('.color-option');
        colorOptions.forEach(opt => opt.classList.remove('selected'));

        const url = new URL(window.location);
        const paramsToRemove = [];
        
        for (const [key] of url.searchParams) {
            if (key !== 'sort_by' && key !== 'page' && key !== 'view' && key !== 'per_page') {
                paramsToRemove.push(key);
            }
        }
        
        paramsToRemove.forEach(param => url.searchParams.delete(param));
        window.history.replaceState({}, '', url);

        this.updateSelectedFilters();
        this.triggerUpdate('clear', {});
    }

    getCurrentFilters() {
        const filters = {};
        
        this.filterSelectors.forEach(filter => {
            const param = filter.dataset.filterParam;
            const value = filter.value;
            
            if (value) {
                filters[param] = value;
            }
        });

        return filters;
    }

    destroy() {
        if (this.sortSelector) {
            this.sortSelector.removeEventListener('change', this.handleSortChange);
        }
        
        this.filterSelectors.forEach(filter => {
            filter.removeEventListener('change', this.handleFilterChange);
        });
    }
}

// Initialize the filter and sort manager
const filterManager = new FilterSortManager({
    sortId: 'sortBy',
    loaderId: 'filterLoader',
    productGridId: 'productGrid',
    onChange: async (type, data, loader) => {
        if (loader) loader.classList.remove("!hidden");
        await filterManager.handleResponse();
    }
});

// Clear filters button functionality
document.addEventListener('click', (e) => {
    if (e.target.matches('[data-clear-filters]')) {
        e.preventDefault();
        filterManager.clearAllFilters();
    }
});